import * as React from "react";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import DashboardShellLayout from "@/app/(dashboard)/layout";
import SettingsSectionLayout from "@/app/(dashboard)/settings/layout";
import Link from "@/components/link";
import { useMessages } from "@/locales";

const LoginPage = React.lazy(() => import("@/app/(auth)/login/page"));
const ForgotPasswordPage = React.lazy(() => import("@/app/(auth)/forgot-password/page"));
const ResetPasswordPage = React.lazy(() => import("@/app/(auth)/reset-password/page"));
const AcceptInvitePage = React.lazy(() => import("@/app/(auth)/accept-invite/page"));
const DashboardPage = React.lazy(() => import("@/app/(dashboard)/page"));
const OrderServicesPage = React.lazy(() => import("@/app/(dashboard)/order-services/page"));
const OrderDetailPage = React.lazy(() => import("@/app/(dashboard)/order-services/[id]/page"));
const NewOrderPage = React.lazy(() => import("@/app/(dashboard)/order-services/new/page"));
const MasterDocumentsPage = React.lazy(() => import("@/app/(dashboard)/master-documents/page"));
const ApprovalsPage = React.lazy(() => import("@/app/(dashboard)/approvals/page"));
const TicketsPage = React.lazy(() => import("@/app/(dashboard)/tickets/page"));
const TicketDetailPage = React.lazy(() => import("@/app/(dashboard)/tickets/[id]/page"));
const AnnouncementsPage = React.lazy(() => import("@/app/(dashboard)/announcements/page"));
const MembersPage = React.lazy(() => import("@/app/(dashboard)/members/page"));
const TeamsPage = React.lazy(() => import("@/app/(dashboard)/teams/page"));
const EndCustomersPage = React.lazy(() => import("@/app/(dashboard)/end-customers/page"));
const StoresPage = React.lazy(() => import("@/app/(dashboard)/stores/page"));
const BillingPage = React.lazy(() => import("@/app/(dashboard)/billing/page"));
const SettingsPage = React.lazy(() => import("@/app/(dashboard)/settings/page"));
const SettingsNotifications = React.lazy(() => import("@/app/(dashboard)/settings/notifications/page"));
const SettingsPassword = React.lazy(() => import("@/app/(dashboard)/settings/password/page"));
const SettingsSecurity = React.lazy(() => import("@/app/(dashboard)/settings/security/page"));
const SettingsSessions = React.lazy(() => import("@/app/(dashboard)/settings/sessions/page"));
const SettingsOrganization = React.lazy(
  () => import("@/app/(dashboard)/settings/organization/page"),
);

function authGuard() {
  if (localStorage.getItem("lwp.session") !== "1") throw redirect({ to: "/login" });
}

function RouteLoading() {
  const m = useMessages();
  return <div className="p-6 text-sm text-muted-foreground">{m.common.loading}</div>;
}

function LazyPage({ page: Page }: { page: React.LazyExoticComponent<React.ComponentType> }) {
  return (
    <React.Suspense fallback={<RouteLoading />}>
      <Page />
    </React.Suspense>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <DashboardShellLayout>{children}</DashboardShellLayout>;
}

function SettingsShell({ children }: { children: React.ReactNode }) {
  return (
    <Shell>
      <SettingsSectionLayout>{children}</SettingsSectionLayout>
    </Shell>
  );
}

function NotFoundPage() {
  const m = useMessages();
  return (
    <div className="mx-auto mt-24 max-w-md p-6 text-center">
      <h1 className="text-2xl font-semibold">{m.common.pageNotFound}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{m.common.pageNotFoundHint}</p>
      <Link className="mt-4 inline-block text-sm underline" href="/">
        {m.common.goHome}
      </Link>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

function publicRoute(path: string, page: React.LazyExoticComponent<React.ComponentType>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => <LazyPage page={page} />,
  });
}

function protectedRoute(path: string, page: React.LazyExoticComponent<React.ComponentType>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: authGuard,
    component: () => (
      <Shell>
        <LazyPage page={page} />
      </Shell>
    ),
  });
}

function settingsRoute(path: string, page: React.LazyExoticComponent<React.ComponentType>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: authGuard,
    component: () => (
      <SettingsShell>
        <LazyPage page={page} />
      </SettingsShell>
    ),
  });
}

const routeTree = rootRoute.addChildren([
  publicRoute("/login", LoginPage),
  publicRoute("/forgot-password", ForgotPasswordPage),
  publicRoute("/reset-password", ResetPasswordPage),
  publicRoute("/accept-invite", AcceptInvitePage),
  // Invitation and reset emails use an /auth prefix. Keep the short paths for in-app links.
  publicRoute("/auth/accept-invite", AcceptInvitePage),
  publicRoute("/auth/reset-password", ResetPasswordPage),
  protectedRoute("/", DashboardPage),
  protectedRoute("/order-services", OrderServicesPage),
  protectedRoute("/order-services/new", NewOrderPage),
  protectedRoute("/order-services/$id", OrderDetailPage),
  protectedRoute("/master-documents", MasterDocumentsPage),
  protectedRoute("/approvals", ApprovalsPage),
  protectedRoute("/tickets", TicketsPage),
  protectedRoute("/tickets/$id", TicketDetailPage),
  protectedRoute("/announcements", AnnouncementsPage),
  protectedRoute("/members", MembersPage),
  protectedRoute("/teams", TeamsPage),
  protectedRoute("/end-customers", EndCustomersPage),
  protectedRoute("/stores", StoresPage),
  protectedRoute("/billing", BillingPage),
  settingsRoute("/settings", SettingsPage),
  settingsRoute("/settings/notifications", SettingsNotifications),
  settingsRoute("/settings/password", SettingsPassword),
  settingsRoute("/settings/security", SettingsSecurity),
  settingsRoute("/settings/sessions", SettingsSessions),
  settingsRoute("/settings/organization", SettingsOrganization),
]);

const router = createRouter({ routeTree });

export function App() {
  return <RouterProvider router={router} />;
}
